import { ComponentRef, ReactElement, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Button,
  Checkbox,
  Dialog,
  ErrorSummary,
  Field,
  Fieldset,
  Heading,
  Input,
  Label,
  Paragraph,
  Select,
  ValidationMessage,
} from '@digdir/designsystemet-react';
import type { HrSafetyReps } from '../../../api/hrSafetyReps';
import {
  useRegisterSafetyRepMutation,
  useUpdateSafetyRepTeamsMutation,
} from '../../../hooks/queries/hr/useHrSafetyRepsQuery';
import {
  candidates,
  emptyForm,
  formErrors,
  NEW_PERSON,
  SafetyRepField,
  SafetyRepForm,
  toggleTeam,
  toRegistration,
} from './safetyRepsView';
import classes from './HRSafetyReps.module.css';

type Props = {
  data: HrSafetyReps;
  /** The team the dialog was opened for */
  teamId?: number | null;
  /** The safety representative whose teams are changed */
  editing?: HrSafetyReps['representatives'][number] | null;
  onClose: () => void;
};

const FIELD_IDS: Record<SafetyRepField, string> = {
  person: 'safety-rep-person',
  email: 'safety-rep-email',
  teams: 'safety-rep-teams',
};

/** HR registers who the employees elected, for which teams, or changes the teams of one */
export const SafetyRepDialog = ({
  data,
  teamId = null,
  editing = null,
  onClose,
}: Props): ReactElement => {
  const { t } = useTranslation();
  const [form, setForm] = useState<SafetyRepForm>(
    editing
      ? { person: String(editing.userId), email: '', teamIds: editing.teamIds }
      : emptyForm(teamId),
  );
  const [shown, setShown] = useState<SafetyRepField[]>([]);
  const summary = useRef<ComponentRef<typeof ErrorSummary>>(null);
  const register = useRegisterSafetyRepMutation();
  const update = useUpdateSafetyRepTeamsMutation();
  const saving = register.isPending || update.isPending;
  const failed = register.isError || update.isError;
  const errorFor = (field: SafetyRepField) =>
    shown.includes(field) ? t(`hrSafetyReps.errors.${field}`) : undefined;

  useEffect(() => {
    if (shown.length > 0) summary.current?.focus();
  }, [shown]);

  const save = () => {
    const errors = formErrors(form, Boolean(editing));
    setShown(errors);
    if (errors.length > 0) return;
    if (editing)
      update.mutate(
        { userId: editing.userId, teamIds: form.teamIds },
        { onSuccess: onClose },
      );
    else register.mutate(toRegistration(form), { onSuccess: onClose });
  };

  return (
    <Dialog
      open
      onClose={onClose}
      closedby="any"
      closeButton={t('hrSafetyReps.cancel')}
    >
      <div className={classes.dialog}>
        <Heading level={2} data-size="xs">
          {editing
            ? t('hrSafetyReps.editTitle', { email: editing.email })
            : t('hrSafetyReps.registerTitle')}
        </Heading>
        <Paragraph data-size="sm" className={classes.subtle}>
          {t('hrSafetyReps.elected')}
        </Paragraph>
        {shown.length > 0 && (
          <ErrorSummary ref={summary}>
            <ErrorSummary.Heading>
              {t('hrSafetyReps.errors.title')}
            </ErrorSummary.Heading>
            <ErrorSummary.List>
              {shown.map((field) => (
                <ErrorSummary.Item key={field}>
                  <ErrorSummary.Link href={`#${FIELD_IDS[field]}`}>
                    {t(`hrSafetyReps.errors.${field}`)}
                  </ErrorSummary.Link>
                </ErrorSummary.Item>
              ))}
            </ErrorSummary.List>
          </ErrorSummary>
        )}
        {!editing && (
          <Field data-size="sm">
            <Label htmlFor={FIELD_IDS.person}>{t('hrSafetyReps.person')}</Label>
            <Select
              id={FIELD_IDS.person}
              value={form.person}
              aria-invalid={Boolean(errorFor('person')) || undefined}
              onChange={(event) =>
                setForm({ ...form, person: event.target.value })
              }
            >
              <Select.Option value="" disabled>
                {t('hrSafetyReps.choosePerson')}
              </Select.Option>
              {candidates(data).map((candidate) => (
                <Select.Option key={candidate.id} value={String(candidate.id)}>
                  {candidate.email}
                </Select.Option>
              ))}
              <Select.Option value={NEW_PERSON}>
                {t('hrSafetyReps.newPerson')}
              </Select.Option>
            </Select>
            {errorFor('person') && (
              <ValidationMessage>{errorFor('person')}</ValidationMessage>
            )}
          </Field>
        )}
        {!editing && form.person === NEW_PERSON && (
          <Field data-size="sm">
            <Label htmlFor={FIELD_IDS.email}>{t('hrSafetyReps.email')}</Label>
            <Field.Description id={`${FIELD_IDS.email}-help`}>
              {t('hrSafetyReps.emailHelp')}
            </Field.Description>
            <Input
              id={FIELD_IDS.email}
              type="email"
              value={form.email}
              aria-describedby={`${FIELD_IDS.email}-help`}
              aria-invalid={Boolean(errorFor('email')) || undefined}
              onChange={(event) =>
                setForm({ ...form, email: event.target.value })
              }
            />
            {errorFor('email') && (
              <ValidationMessage>{errorFor('email')}</ValidationMessage>
            )}
          </Field>
        )}
        <Fieldset data-size="sm" id={FIELD_IDS.teams}>
          <Fieldset.Legend>{t('hrSafetyReps.teams')}</Fieldset.Legend>
          <div className={classes.teamChoices}>
            {data.teams.map((team) => (
              <Checkbox
                key={team.id}
                label={team.name}
                checked={form.teamIds.includes(team.id)}
                aria-invalid={Boolean(errorFor('teams')) || undefined}
                onChange={() => setForm(toggleTeam(form, team.id))}
              />
            ))}
          </div>
          {errorFor('teams') && (
            <ValidationMessage>{errorFor('teams')}</ValidationMessage>
          )}
        </Fieldset>
        {failed && (
          <Alert data-color="danger" data-size="sm">
            <Paragraph data-size="sm">{t('hrSafetyReps.failed')}</Paragraph>
          </Alert>
        )}
        <div className={classes.buttons}>
          <Button data-size="sm" loading={saving} onClick={save}>
            {t(editing ? 'hrSafetyReps.save' : 'hrSafetyReps.register')}
          </Button>
          <Button variant="tertiary" data-size="sm" onClick={onClose}>
            {t('hrSafetyReps.cancel')}
          </Button>
        </div>
      </div>
    </Dialog>
  );
};
